import React from "react";
import { Globe2, GraduationCap, Plane, Award, Sparkles, CheckCircle2 } from "lucide-react";

/**
 * Curated list of top international study abroad & career destinations
 * where Crescet Edustreams students have received admissions, scholarships, and visas.
 */
export const GLOBAL_DESTINATIONS = [
  // --- Row 1: North America, UK & Western Europe ---
  {
    id: "usa",
    code: "us",
    name: "United States",
    region: "North America",
    universities: "Ivy League, MIT, Stanford, CMU",
    highlight: "STEM OPT & Top Tier R&D",
    badge: "500+ Admits",
    emoji: "🇺🇸",
  },
  {
    id: "uk",
    code: "gb",
    name: "United Kingdom",
    region: "United Kingdom",
    universities: "Oxford, Cambridge, Imperial, LSE",
    highlight: "Russell Group & 2-Yr PSW",
    badge: "Top Choice",
    emoji: "🇬🇧",
  },
  {
    id: "canada",
    code: "ca",
    name: "Canada",
    region: "North America",
    universities: "U of Toronto, McGill, UBC, Waterloo",
    highlight: "PGWP & Express Entry Route",
    badge: "High Visa Rate",
    emoji: "🇨🇦",
  },
  {
    id: "germany",
    code: "de",
    name: "Germany",
    region: "Western Europe",
    universities: "TU Munich, Heidelberg, RWTH Aachen",
    highlight: "TU9 STEM & Low/Zero Tuition",
    badge: "Free Tuition",
    emoji: "🇩🇪",
  },
  {
    id: "australia",
    code: "au",
    name: "Australia",
    region: "Asia-Pacific",
    universities: "Melbourne, Sydney, UNSW, ANU",
    highlight: "Group of Eight (Go8) & Post-Study Work",
    badge: "Top Go8 Admits",
    emoji: "🇦🇺",
  },
  {
    id: "ireland",
    code: "ie",
    name: "Ireland",
    region: "European Union",
    universities: "Trinity College Dublin, UCD",
    highlight: "European Tech & Pharma Capital",
    badge: "Silicon Docks",
    emoji: "🇮🇪",
  },
  {
    id: "france",
    code: "fr",
    name: "France",
    region: "Western Europe",
    universities: "INSEAD, HEC Paris, Sorbonne",
    highlight: "Global Top MBAs & Luxury/Fashion",
    badge: "Top Business",
    emoji: "🇫🇷",
  },
  {
    id: "netherlands",
    code: "nl",
    name: "Netherlands",
    region: "Western Europe",
    universities: "TU Delft, Univ of Amsterdam, Leiden",
    highlight: "Top English-Taught Programs & Tech",
    badge: "Innovation Hub",
    emoji: "🇳🇱",
  },
  {
    id: "switzerland",
    code: "ch",
    name: "Switzerland",
    region: "Central Europe",
    universities: "ETH Zurich, EPFL, Univ of Zurich",
    highlight: "World #1 in Innovation & Precision",
    badge: "Premier STEM",
    emoji: "🇨🇭",
  },
  {
    id: "singapore",
    code: "sg",
    name: "Singapore",
    region: "Asia-Pacific",
    universities: "NUS, NTU, SMU",
    highlight: "Global Top 15 Universities & Finance Hub",
    badge: "Asian Gateway",
    emoji: "🇸🇬",
  },
  {
    id: "sweden",
    code: "se",
    name: "Sweden",
    region: "Scandinavia",
    universities: "KTH Royal Institute, Lund, Karolinska",
    highlight: "World Leader in Sustainability & Tech",
    badge: "Nordic Tech",
    emoji: "🇸🇪",
  },
  {
    id: "newzealand",
    code: "nz",
    name: "New Zealand",
    region: "Oceania",
    universities: "Univ of Auckland, Otago, Victoria",
    highlight: "Exceptional Quality of Life & Research",
    badge: "Work Pathways",
    emoji: "🇳🇿",
  },
  {
    id: "italy",
    code: "it",
    name: "Italy",
    region: "Southern Europe",
    universities: "Politecnico di Milano, Bocconi",
    highlight: "Premier Design, Architecture & Finance",
    badge: "EU Scholarships",
    emoji: "🇮🇹",
  },

  // --- Row 2: Central Europe, Nordics, Asia-Pacific & Emerging Hubs ---
  {
    id: "japan",
    code: "jp",
    name: "Japan",
    region: "East Asia",
    universities: "Univ of Tokyo, Kyoto, Tokyo Tech",
    highlight: "MEXT Scholarships & Advanced Robotics",
    badge: "MEXT Scholars",
    emoji: "🇯🇵",
  },
  {
    id: "uae",
    code: "ae",
    name: "United Arab Emirates",
    region: "Middle East",
    universities: "NYU Abu Dhabi, Heriot-Watt Dubai",
    highlight: "Tax-Free Careers & Global Campuses",
    badge: "Fast Visa",
    emoji: "🇦🇪",
  },
  {
    id: "spain",
    code: "es",
    name: "Spain",
    region: "Southern Europe",
    universities: "IE University, ESADE, IESE",
    highlight: "Triple Crown Business Schools",
    badge: "Global MBAs",
    emoji: "🇪🇸",
  },
  {
    id: "southkorea",
    code: "kr",
    name: "South Korea",
    region: "East Asia",
    universities: "SNU, KAIST, Yonsei, Korea Univ",
    highlight: "GKS Government Scholarships & Semiconductors",
    badge: "High Tech",
    emoji: "🇰🇷",
  },
  {
    id: "denmark",
    code: "dk",
    name: "Denmark",
    region: "Scandinavia",
    universities: "Univ of Copenhagen, DTU, Aarhus",
    highlight: "Green Energy, Life Sciences & Work-Life",
    badge: "Top Biotech",
    emoji: "🇩🇰",
  },
  {
    id: "finland",
    code: "fi",
    name: "Finland",
    region: "Nordic",
    universities: "Aalto University, Univ of Helsinki",
    highlight: "Global Benchmark in Education & Tech",
    badge: "Education Leader",
    emoji: "🇫🇮",
  },
  {
    id: "norway",
    code: "no",
    name: "Norway",
    region: "Nordic",
    universities: "Univ of Oslo, NTNU, BI Norwegian",
    highlight: "Renewable Energy & Maritime Research",
    badge: "Clean Tech",
    emoji: "🇳🇴",
  },
  {
    id: "austria",
    code: "at",
    name: "Austria",
    region: "Central Europe",
    universities: "Univ of Vienna, TU Wien",
    highlight: "Affordable High-Ranked Central European Hub",
    badge: "Low Tuition",
    emoji: "🇦🇹",
  },
  {
    id: "belgium",
    code: "be",
    name: "Belgium",
    region: "Western Europe",
    universities: "KU Leuven, Ghent University",
    highlight: "Heart of Europe & Renowned Research Hub",
    badge: "EU Center",
    emoji: "🇧🇪",
  },
  {
    id: "hongkong",
    code: "hk",
    name: "Hong Kong",
    region: "Asia-Pacific",
    universities: "HKU, HKUST, CUHK",
    highlight: "Global Financial Gateway & Generous Fellowships",
    badge: "Top 50 Ranked",
    emoji: "🇭🇰",
  },
  {
    id: "poland",
    code: "pl",
    name: "Poland",
    region: "Central Europe",
    universities: "Univ of Warsaw, Jagiellonian Univ",
    highlight: "Affordable Medical & Engineering Degrees",
    badge: "Budget Friendly",
    emoji: "🇵🇱",
  },
  {
    id: "malaysia",
    code: "my",
    name: "Malaysia",
    region: "Southeast Asia",
    universities: "Univ of Malaya, Monash, Nottingham",
    highlight: "Top UK/Australian Twinning Degrees",
    badge: "Global Degrees",
    emoji: "🇲🇾",
  },
  {
    id: "czechia",
    code: "cz",
    name: "Czech Republic",
    region: "Central Europe",
    universities: "Charles University, CTU in Prague",
    highlight: "Historic European Medicine & Tech Tradition",
    badge: "High ROI",
    emoji: "🇨🇿",
  },
];

const ROW_1 = GLOBAL_DESTINATIONS.slice(0, 13);
const ROW_2 = GLOBAL_DESTINATIONS.slice(13, 26);

function CountryCard({ country }) {
  // Use flagcdn.com high-res flag icons with instant fallback
  const flagImgSrc = `https://flagcdn.com/w80/${country.code}.png`;
  const flagImgSrcSet = `https://flagcdn.com/w160/${country.code}.png 2x`;

  return (
    <div
      className="group relative flex h-14 sm:h-16 items-center gap-3.5 rounded-2xl border border-slate-200/90 bg-white/95 px-4 sm:px-5 shadow-2xs backdrop-blur-sm transition-all duration-300 hover:border-[#0B5D4B]/40 hover:bg-white hover:shadow-md hover:scale-[1.03] cursor-default select-none shrink-0"
      title={`${country.name} — ${country.universities} (${country.highlight})`}
    >
      {/* National Flag (Direct, no outer container) */}
      <div className="relative shrink-0 transition-transform duration-300 group-hover:scale-105">
        <img
          src={flagImgSrc}
          srcSet={flagImgSrcSet}
          alt={`${country.name} flag`}
          loading="lazy"
          className="h-6 w-9 sm:h-6.5 sm:w-10 object-cover rounded-md shadow-xs border border-slate-200/90"
          onError={(e) => {
            // If image fails, show emoji fallback
            e.target.style.display = "none";
            const fallback = e.target.parentElement.querySelector(".flag-emoji-fallback");
            if (fallback) fallback.style.display = "flex";
          }}
        />
        <div
          className="flag-emoji-fallback hidden items-center justify-center text-2xl"
          aria-hidden="true"
        >
          {country.emoji}
        </div>
      </div>

      {/* Country Name & Details */}
      <div className="flex flex-col text-left justify-center">
        <div className="flex items-center gap-2">
          <span className="text-xs sm:text-sm font-bold text-slate-900 tracking-tight whitespace-nowrap transition-colors duration-200 group-hover:text-[#0B5D4B]">
            {country.name}
          </span>
          <span className="inline-flex items-center rounded-full bg-[#0B5D4B]/10 px-2 py-0.5 text-[9px] font-semibold text-[#0B5D4B] whitespace-nowrap">
            {country.badge}
          </span>
        </div>

        <span className="text-[10px] font-medium text-slate-500 whitespace-nowrap">
          {country.universities}
        </span>
      </div>
    </div>
  );
}

export default function CountriesMarquee() {
  return (
    <section className="relative w-full overflow-hidden bg-white py-14 sm:py-20 border-b border-slate-200/80">
      {/* Ambient background glow accents */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 right-1/4 h-80 w-80 rounded-full bg-[#0B5D4B]/5 blur-3xl" />
        <div className="absolute -bottom-24 left-1/4 h-80 w-80 rounded-full bg-[#C89B3C]/5 blur-3xl" />
      </div>

      {/* Section Header */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center mb-10 sm:mb-14">
        <div className="mb-3.5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50/80 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#0B5D4B] shadow-2xs backdrop-blur">
          <Globe2 size={14} className="text-[#0B5D4B]" />
          <span>Global Study Destinations</span>
        </div>

        <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
          We Have Sent Students to The World
        </h2>

        <p className="mx-auto mt-4 max-w-3xl text-sm sm:text-base leading-relaxed text-slate-600">
          From top Ivy League institutions in the US to prestigious European research hubs and Asia-Pacific powerhouses, our students are thriving across <strong className="font-semibold text-slate-900">25+ countries worldwide</strong>.
        </p>

        {/* Highlight Badges */}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5 sm:gap-4 text-xs font-semibold text-slate-500">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1">
            <GraduationCap size={13} className="text-[#0B5D4B]" /> Top 100 QS Ranked Universities
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1">
            <Plane size={13} className="text-[#C89B3C]" /> 98% Visa & Scholarship Success
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1">
            <Award size={13} className="text-[#0B5D4B]" /> Post-Study Work & PR Pathways
          </span>
        </div>
      </div>

      {/* Full-Width Dual Marquee Container */}
      <div className="relative w-full space-y-4 sm:space-y-5 overflow-hidden">
        {/* Left & Right Edge Fade Masks */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 z-10 w-16 sm:w-36 bg-gradient-to-r from-white via-white/80 to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-16 sm:w-36 bg-gradient-to-l from-white via-white/80 to-transparent" />

        {/* Row 1: Moving Left (Duplicated for continuous seamless loop) */}
        <div className="flex overflow-hidden">
          <div className="animate-marquee-left flex gap-3.5 sm:gap-4.5 pr-3.5 sm:pr-4.5">
            {[...ROW_1, ...ROW_1].map((country, index) => (
              <CountryCard key={`row1-${country.id}-${index}`} country={country} />
            ))}
          </div>
        </div>

        {/* Row 2: Moving Right (Duplicated for continuous seamless loop) */}
        <div className="flex overflow-hidden">
          <div className="animate-marquee-right flex gap-3.5 sm:gap-4.5 pr-3.5 sm:pr-4.5">
            {[...ROW_2, ...ROW_2].map((country, index) => (
              <CountryCard key={`row2-${country.id}-${index}`} country={country} />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Trust & Verification Note */}
      <div className="mt-8 text-center">
        <p className="text-xs font-medium text-slate-400">
          Hover over any country to pause • Complete end-to-end guidance from profile evaluation to pre-departure briefing
        </p>
      </div>
    </section>
  );
}
